import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import store, { borrowerActions } from '@apply/adminStore';
import { formatPhone } from '../../../common/SharedFunctions';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';

export const PrincipalSelector = ({
    principalId,
    excludedEntityId = 0,
    onSelect = null,
    disabled = false,
    required = true,
    canEdit = true,
    showTitle = true,
    title = 'Principal',
}) => {
    const PER_PAGE = 20;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const { dispatch } = store;

    //Borrower Search
    const [cache, set_cache] = useState({});
    const [searchLoading, set_searchLoading] = useState(false);
    const [principals, set_principals] = useState([]);
    const [query, set_query] = useState('');
    const [principal, set_principal] = useState();

    useEffect(() => {
        async function principalIdChanged() {
            if (!principalId) {
                set_principal(null);
                return;
            }

            const response = await borrowerApi.principalsTypeAheadById(principalId);
            if (response.statusCode !== 200) return;

            const bwr = response.result;
            set_principal(bwr);
        }

        principalIdChanged();
    }, [principalId]);

    const addContact = () => {
        dispatch(
            borrowerActions.setEditPrincipalModal({
                editPrincipalModal: {
                    isOpen: true,
                    principalId: 0,
                    onChange: afterSave,
                },
            })
        );
    };

    const doSearch = async (query, page = 1) => {
        const model = {
            searchText: query,
            take: PER_PAGE,
            skip: (page - 1) * PER_PAGE,
            excludedEntityId,
        };
        return await borrowerApi.principalsTypeAhead(model);
    };

    const handleInputChange = (q) => {
        set_principal();
        q = (q ?? '').toLowerCase();
        set_query(q);
    };

    const handleSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (cache[q]) {
            set_principals(cache[q].options);
            return;
        }

        set_searchLoading(true);
        doSearch(q).then((resp) => {
            cache[q] = { options: resp.result, page: 1 };
            set_cache(cache);
            set_searchLoading(false);
            set_principals(resp.result);
        });
    }, []);

    const handlePagination = (e, shownResults) => {
        const cachedQuery = cache[query];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_searchLoading(true);
        const page = cachedQuery.page + 1;
        doSearch(query, page).then((resp) => {
            const options = cachedQuery.options.concat(resp.result);
            cache[query] = { ...cachedQuery, options, page };
            set_cache(cache);
            set_searchLoading(false);
            set_principals(options);
        });
    };

    const selectContact = (contact) => {
        set_principal(contact);
        onSelect && onSelect(contact, cancelSelect);
    };

    const cancelSelect = () => {
        set_principal(null);
    };

    const clearContact = () => {
        set_principal(null);
        onSelect && onSelect(null, cancelSelect);
    };

    const afterSave = (contact) => {
        selectContact(contact);
        set_cache({});
    };

    const editPrincipal = (principalId) => {
        dispatch(
            borrowerActions.setEditPrincipalModal({
                editPrincipalModal: {
                    isOpen: true,
                    principalId,
                    onChange: afterSave,
                },
            })
        );
    };

    const filterByFields = ['name', 'email', 'userName'];

    return (
        <div>
            <div className='type-ahead-container'>
                {showTitle && (
                    <label htmlFor='borrower-search'>
                        {title} {required && <span className='req'>*</span>}
                    </label>
                )}
                <AsyncTypeahead
                    className='type-ahead'
                    id='borrower-search'
                    isLoading={searchLoading}
                    labelKey={(option) => `${option?.name}`}
                    maxResults={PER_PAGE - 1}
                    minLength={2}
                    onInputChange={handleInputChange}
                    onPaginate={handlePagination}
                    onSearch={handleSearch}
                    onChange={(event) => selectContact(event[0])}
                    options={principals}
                    paginate
                    placeholder='Search on Name or Email...'
                    renderMenuItemChildren={(option) => (
                        <div
                            key={option.id}
                            style={{ borderBottom: '1px solid silver' }}
                        >
                            <span>
                                <div>
                                    <b>{option.name}</b> {option.email && <span>({option.email})</span>}
                                </div>

                                {option.city && (
                                    <div>
                                        {option.city}, {option.state}
                                    </div>
                                )}
                            </span>
                        </div>
                    )}
                    useCache={false}
                    option
                    selected={principal ? [principal] : []}
                    disabled={disabled}
                    inputProps={{ required: required }}
                    filterBy={filterByFields}
                />
                <button
                    type='button'
                    className='type-ahead-clear-button'
                    onClick={() => clearContact()}
                    disabled={disabled}
                    title='Remove selected contact'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <button
                    type='button'
                    onClick={addContact}
                    className='button type-ahead-add-button'
                    title='Add a new contact'
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            </div>

            {principal && (
                <div
                    style={{
                        border: '1px solid silver',
                        borderRadius: '10px',
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                        width: '100%',
                    }}
                >
                    <div className='row'>
                        <div className='columns small-12 large-11'>
                            {principal.name && (
                                <div>
                                    <b>{principal.name}</b>
                                </div>
                            )}
                            {principal.street1 && <div>{principal.street1}</div>}
                            {principal.street2 && <div>{principal.street2}</div>}
                            {principal.city && principal.state && principal.zipCode && (
                                <div>
                                    {principal.city}, {principal.state} {principal.zipCode}
                                </div>
                            )}
                            {principal.email && <div>{principal.email}</div>}
                            {principal.phone && <div>{formatPhone(principal.phone)}</div>}
                        </div>
                        {canEdit && (
                            <div className='columns small-12 large-1'>
                                <button
                                    type='button'
                                    onClick={() => editPrincipal(principal.principalId)}
                                    className='button hollow'
                                    title='Edit Borrower Details'
                                    style={{
                                        verticalAlign: 'unset',
                                        marginBottom: '0',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPencil} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
